import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api.js';
import PhotoUploader from '../components/PhotoUploader.jsx';
import ClassificationFields from '../components/ClassificationFields.jsx';

//const CATEGORIES = ['Electronics','Jewelry','Documents','Bags','Bike & Outdoor','Vehicle','Sentimental','Other'];

const empty = {
  name: '', 
  description: '', 
  category: 'Other',
  subcategory: '',
  tags: [],             
  bountyDollars: '25', currency: 'USD',
  identifyingMarks: '',
  status: 'ACTIVE',
  photos: [],
};

export default function ItemEditor() {
  const { itemId } = useParams();
  const editing = !!itemId;
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]); // { key, preview/url }
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!editing);

  useEffect(() => {
    if (!editing) return;
    api.getItem(itemId).then((it) => {
      setForm({
        name: it.name,
        description: it.description ?? '',
        category: it.category,
        subcategory: it.subcategory ?? '',
        tags: it.tags ?? [],
        bountyDollars: ((it.bountyCents ?? 0) / 100).toFixed(2),
        currency: it.currency,
        identifyingMarks: it.identifyingMarks ?? '',
        status: it.status,
        photos: it.photos ?? [],
      });
      setPhotos((it.photos ?? []).map((p) => ({
        key: p.key,
        preview: p.urls?.thumb ?? p.urls?.display ?? p.urls?.original,
      })));
      setLoaded(true);
      }).catch((e) => setErr(e.message));
      }, [editing, itemId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function onSubmit(e) {
    e.preventDefault();
    if (!photos || photos.length === 0) {
    setErr('At least one photo is required.');
    return;}
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category,
        subcategory: form.subcategory,
        tags: form.tags,
        bountyCents: Math.round(parseFloat(form.bountyDollars) * 100),
        currency: form.currency,
        identifyingMarks: form.identifyingMarks.trim(),
        status: form.status,
        photoKeys: photos.map((p) => p.key),
      };
      const saved = editing
        ? await api.updateItem(itemId, body)
        : await api.createItem(body);
      nav(`/items/${saved.itemId}`);
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }

  async function onDelete() {
    if (!confirm('Delete this item permanently?')) return;
    await api.deleteItem(itemId);
    nav('/dashboard');
  }

  if (!loaded) return <div className="loading-block muted center">Loading…</div>;

  return (
    <div className="container-narrow page">
      <span className="eyebrow">{editing ? 'Edit item' : 'New item'}</span>
      <h2 className="mt-8 mb-28">
        {editing ? form.name || 'Edit' : 'What would you hate to lose?'}
      </h2>

      <div className="card">
        {err && <div className="alert error mb-18">{err}</div>}
        <form onSubmit={onSubmit}>
          <div className="field">
            <label>Name <span className="muted tiny">(required)</span></label>
            <input required maxLength={120} value={form.name} onChange={set('name')} placeholder="e.g. Grandfather's pocket watch" />
          </div>

          <ClassificationFields
            category={form.category}
            subcategory={form.subcategory}
            tags={form.tags}
            onCategory={(v) => setForm((f) => ({ ...f, category: v }))}
            onSubcategory={(v) => setForm((f) => ({ ...f, subcategory: v }))}
            onTags={(v) => setForm((f) => ({ ...f, tags: v }))}
          />

          <div className="field-row cols-3">
            <div className="field">
              <label>Bounty <span className="muted tiny">(required)</span></label>
              <input type="number" step="0.01" min="0" required value={form.bountyDollars} onChange={set('bountyDollars')} />
              <div className="hint">Amount you'll pay a finder.</div>
            </div>
            <div className="field">
              <label>Currency</label>
              <select value={form.currency} onChange={set('currency')}>
                <option>USD</option><option>EUR</option><option>GBP</option><option>CAD</option><option>AUD</option><option>JPY</option>
              </select>
            </div>
            <div className="field">
              <label>Status</label>
              <select value={form.status} onChange={set('status')}>
                <option value="ACTIVE">Active (in possession)</option>
                <option value="LOST">Lost</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label>Description</label>
            <textarea maxLength={2000} value={form.description} onChange={set('description')} placeholder="Make, model, colour, age…" />
          </div>

          <div className="field">
            <label>Identifying marks (private)</label>
            <textarea maxLength={500} value={form.identifyingMarks} onChange={set('identifyingMarks')}
              placeholder="A scratch on the back. A name engraved inside the band. Only you see this — used to verify a finder's claim." />
            <div className="hint">This is never shown to finders. Use it to verify their photo really shows your item.</div>
          </div>

          <PhotoUploader value={photos} onChange={setPhotos} />

          <div className="form-actions">
            <button className="btn btn-primary flex-1" disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add to inventory'}
            </button>
            {editing && (
              <button type="button" onClick={onDelete} className="btn btn-danger">Delete</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
